import { useState, useEffect, useCallback, useMemo } from 'react';
import { projectService } from '../services/projectService';

/**
 * Hook to manage project transaction history and financial summary.
 */
export const useProjectTransactions = (projectId, isOpen, refreshTrigger) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [recipientFilter, setRecipientFilter] = useState('all');

  const fetchTransactions = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await projectService.getProjectTransactions(projectId);
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch transactions', err);
      setError(err?.response?.data?.message || err.message || 'Failed to fetch transaction history');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchTransactions();
    }
  }, [isOpen, projectId, fetchTransactions, refreshTrigger]);

  const allTxs = useMemo(() => summary?.transactions || [], [summary]);

  const recipientOptions = useMemo(() => {
    const set = new Set();
    for (const tx of allTxs) {
      const r = tx.recipient || tx.expenseToWhom;
      if (r) set.add(r);
    }
    return Array.from(set).sort();
  }, [allTxs]);

  const filteredTxs = useMemo(() => {
    if (recipientFilter === 'all') return allTxs;
    return allTxs.filter(tx => (tx.recipient || tx.expenseToWhom) === recipientFilter);
  }, [allTxs, recipientFilter]);

  return {
    loading,
    summary,
    error,
    allTxs,
    filteredTxs,
    recipientOptions,
    recipientFilter,
    setRecipientFilter,
    refresh: fetchTransactions
  };
};
